<template>
  <div class="chart-wrapper">
    <div class="chart-header" v-if="title">
      <h3 class="chart-title">{{ title }}</h3>
      <slot name="header-actions"></slot>
    </div>
    <canvas ref="chartEl" :id="chartId"></canvas>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch } from 'vue';
import { Chart, registerables } from 'chart.js';

Chart.register(...registerables);

const props = defineProps({
  chartId: {
    type: String,
    default: () => `chart-${Math.random().toString(36).slice(2)}`,
  },
  title: {
    type: String,
    default: '',
  },
  type: {
    type: String,
    default: 'line',
  },
  data: {
    type: Object,
    required: true,
  },
  options: {
    type: Object,
    default: () => ({}),
  },
});

const chartEl = ref(null);
let chart = null;

const defaultOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: true,
      position: 'top',
      labels: {
        color: getComputedStyle(document.documentElement).getPropertyValue('--text-secondary'),
      },
    },
    tooltip: {
      backgroundColor: getComputedStyle(document.documentElement).getPropertyValue('--bg-tertiary'),
      titleColor: getComputedStyle(document.documentElement).getPropertyValue('--text-primary'),
      bodyColor: getComputedStyle(document.documentElement).getPropertyValue('--text-secondary'),
      borderColor: getComputedStyle(document.documentElement).getPropertyValue('--border-color'),
      borderWidth: 1,
    },
  },
  scales: {
    x: {
      grid: {
        color: getComputedStyle(document.documentElement).getPropertyValue('--border-color'),
      },
      ticks: {
        color: getComputedStyle(document.documentElement).getPropertyValue('--text-muted'),
      },
    },
    y: {
      grid: {
        color: getComputedStyle(document.documentElement).getPropertyValue('--border-color'),
      },
      ticks: {
        color: getComputedStyle(document.documentElement).getPropertyValue('--text-muted'),
      },
    },
  },
};

const initChart = () => {
  if (!chartEl.value) return;

  const mergedOptions = {
    ...defaultOptions,
    ...props.options,
  };

  chart = new Chart(chartEl.value, {
    type: props.type,
    data: props.data,
    options: mergedOptions,
  });
};

const updateChart = () => {
  if (!chart) return;

  chart.data = props.data;
  chart.update('none');
};

watch(() => props.data, updateChart, { deep: true });

onMounted(() => {
  initChart();
});

onUnmounted(() => {
  if (chart) {
    chart.destroy();
    chart = null;
  }
});
</script>

<style scoped>
.chart-wrapper {
  background: rgba(252, 250, 246, 0.98);
  border-radius: 16px;
  padding: 1rem;
  border: 1px solid var(--border-color);
  box-shadow: var(--panel-shadow);
  backdrop-filter: blur(8px);
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--spacing-md);
}

.chart-title {
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-primary);
}

.chart-wrapper canvas {
  max-height: 400px;
}

@media (max-width: 640px) {
  .chart-wrapper {
    padding: 0.75rem;
    border-radius: 12px;
  }

  .chart-wrapper canvas {
    max-height: 280px;
  }
}
</style>
